<template>
    <div class="animated fadeIn">
        <LoadingWidget v-if="initStatus != 1" :status="initStatus" @init="init" />
        <b-card v-else>
            <template v-slot:header>
                <h5>Audit Rekening</h5>
            </template>

            <b-row class="mb-3">
                <b-col cols="12" md="6">
                    <b-form-group label="No. Anggota">
                        <b-form-input
                            v-model="filters.noNasabah"
                            id="filterInput"
                            type="search"
                            placeholder="Masukkan No. Anggota"
                            @keyup.enter="onSearch()"
                        />
                    </b-form-group>
                </b-col>
                <b-col cols="12" md="6">
                    <b-form-group label="Status">
                        <b-form-select
                            v-model="filters.status"
                            id="filterStatus"
                            name="status"
                            :options="optionsStatus"
                        />
                    </b-form-group>
                </b-col>
                <b-col cols="12" md="12" lg="6">
                    <b-form-group label="Tanggal">
                        <div class="d-flex" :class="isMobile ? 'flex-wrap' : ''">
                            <b-form-input
                                v-model="filters.startDate"
                                name="filterFromDate"
                                id="filterDate"
                                type="date"
                                class="flex-fill"
                            ></b-form-input>
                            <span class="ml-2 mr-2 d-flex align-items-center">s/d</span>
                            <b-form-input 
                                v-model="filters.endDate"
                                name="filterToDate"
                                type="date"
                                class="flex-fill"
                            ></b-form-input>
                        </div>
                    </b-form-group>
                </b-col>
                <b-col cols="12" md="12">
                    <div class="d-flex flex-wrap justify-content-between gap-2">
                        <div class="d-flex flex-wrap gap-2">
                            <b-button variant="pgiBtn" :disabled="isBusy" class="mr-2" @click="onSearch()"> 
                                Search
                            </b-button>
                            <b-button variant="pgiBtn" :disabled="isBusy" @click="onReset()">Reset</b-button>
                        </div>
                        <div>
                            <b-button variant="pgiBtn" :disabled="isBusy" @click="confirmDownloadExcel">
                                <i class="fa fa-download mr-2"></i>
                                Download Excel
                            </b-button>
                        </div>
                    </div>
                </b-col>
            </b-row>

            <div class="detail-data-wrapper">
                <b-table bordered hover show-empty 
                    :busy="isBusy" 
                    :items="data"
                    :no-local-sorting="false" 
                    :fields="fields"
                    responsive class="mb-3">
                    <template v-slot:table-busy>
                        <div class="text-center text-danger my-2">
                            <b-spinner class="align-middle"></b-spinner>
                            <strong>Loading data...</strong>
                        </div>
                    </template>
                    <template v-slot:empty>
                        Tidak ada data rekening.
                    </template>
                    <template v-slot:cell(dataNasabah)="row">
                        <p class="mb-0 normalText"><span class="text-muted">No. Anggota:</span> {{row.item.nasabahNo}}</p>
                        <p class="mb-0 normalText"><span class="text-muted">Nama Anggota:</span> {{row.item.nasabahName}}</p>
                    </template>
                    <template v-slot:cell(informasiRekening)="row">
                        <p class="mb-0 normalText"><span class="text-muted">Bank:</span> {{row.item.bankName}}</p>
                        <p class="mb-0 normalText"><span class="text-muted">Nama Rekening:</span> {{row.item.name}}</p>
                        <p class="mb-0 normalText"><span class="text-muted">Nomor Rekening:</span> {{row.item.number}}</p>
                        <p class="mb-0 normalText">
                            <span class="text-muted">Status Rekening: </span>
                            <span class="status-box-md status-box-success" v-if="row.item.isActive">Aktif</span>
                            <span class="status-box-md status-box-danger" v-else>Non-aktif</span>
                        </p>
                    </template>
                    <template v-slot:cell(statusVerifikasi)="row">
                        <p class="mb-0">
                            <span class="text-muted">Status: </span>
                            <span
                                :class="getStatus(row, row.item.isActive).class"
                            >
                                {{ getStatus(row, row.item.isActive).text }}
                            </span>
                        </p>
                        <p class="mb-0"><span class="text-muted">Dibuat oleh:</span> {{row.item.createdBy}}</p>
                        <p class="mb-0">
                            <span class="text-muted">Dibuat pada: </span>
                            <span>
                                {{ $helper.changeDateFormat(row.item.createdAt) }} {{ $helper.changeDateFormatGetHour(row.item.createdAt) }}
                            </span>
                        </p>
                        <p class="mb-0"><span class="text-muted">Diverifikasi oleh:</span> {{row.item.verifiedBy || '-'}}</p>
                        <p class="mb-0">
                            <span class="text-muted">Diverifikasi pada: </span>
                            <span v-if="row.item.verifiedAt">
                                {{ $helper.changeDateFormat(row.item.verifiedAt) }} {{ $helper.changeDateFormatGetHour(row.item.verifiedAt) }}
                            </span>
                            <span v-else>
                                -
                            </span>
                        </p>
                    </template>
                    <template v-slot:cell(action)="row">
                        <div class="d-flex flex-wrap" style="gap: 8px;">
                            <b-button
                                v-if="getStatus(row, row.item.isActive).text == 'Pending'"
                                size="sm" variant="success"
                                @click="onShowModalApprove(row.item)"
                            >
                                <i class="fa fa-check"></i>
                            </b-button>
                        </div>
                    </template>
                </b-table>

                <div class="pagination-wrapper d-flex align-items-center justify-content-between">
                    <b-input-group prepend="Tampilkan" class="align-items-center tableShowItem">
                        <b-form-select v-model="show" @change="onLimitChange" :options="optionsLimit">
                            <template v-slot:first>
                                <b-form-select-option :value="null" disabled />
                            </template>
                        </b-form-select>
                        <span class="ml-2">{{ totalViewText }}</span>
                    </b-input-group>
                    <b-pagination class="mb-0"
                        v-model="currentPage"
                        :total-rows="totalRows"
                        :per-page="perPage"
                        aria-controls="my-table"
                        @input="onPageChange"
                    ></b-pagination>
                </div>
            </div>
        </b-card>
        
        <ModalConfirm
            id="modal-approve"
            title="Konfirmasi Pemeriksaan Rekening"
            :isSubmitting="isLoadingApprove"
            desc="Apakah Anda yakin rekening ini sudah diperiksa dan valid?"
            @submit="onApprove"
        />

        <ModalConfirm id="modal-confirm-excel" title="Konfirmasi Download Excel" :isSubmitting="isLoadingExcel"
            desc="Apakah Anda yakin ingin mendownload audit rekening dalam format Excel?"
            @submit="openModalDownloadExcel" />

        <b-modal id="modal-download-excel" centered title="Download Excel" hide-footer no-close-on-backdrop>
            <div class="show-btn-download">
                <p class="">
                Silakan download dengan klik tombol dibawah ini :
                </p>
                <b-button variant="pgiBtn" class="mb-0 download-btn">
                <a v-bind:href="$baseFilePath + filePathDownload" download>
                    <i class="fa fa-download mr-2"></i>
                    Download Excel
                </a>
                </b-button>
            </div>
        </b-modal>
    </div>
</template>

<script>
import CONSTANTS from '@/constants'

export default {
    name: 'audit-rekening',
    data() {
        return {
            initStatus: 0,
            isBusy: true,
            data: [],
            fields: [
                { key: "dataNasabah", label: "Data Nasabah"},
                { key: "informasiRekening", label: "Informasi Rekening" },
                { key: "statusVerifikasi", label: "Informasi Riwayat"},
                { key: "action", label: "Aksi", thClass: 'text-center', tdClass: 'text-center' },
            ],
            filters: {
                noNasabah: '',
                status: '',
                startDate: null,
                endDate: null,
            },
            optionsStatus: [
                {
                    value: '',
                    text: 'Semua'
                },
                {
                    value: false,
                    text: 'Pending'
                },
                {
                    value: true,
                    text: 'Verified'
                },
            ],
            currentPage: 1,
            show: 10,
            perPage: 10,
            totalRows: 0,
            totalPages: 0,
            totalViewText: "",
            optionsLimit: CONSTANTS.OPTIONS_LIMIT_PAGINATION,
            isMobile: false,
            isLoadingApprove: false,
            selectedRow: null,
            filePathDownload: '',
            isLoadingExcel: false,
        }
    },
    methods: {
        async init() {
            this.initStatus = 0
            try {
                await this.getItems()
                this.initStatus = 1
            } catch (error) {
                this.$helper.parseError(this, error)
                this.initStatus = -1
            }
        },
        getItems(page = 1) {
            this.isBusy = true
            this.data = []
            this.currentPage = isNaN(page) ? 1 : page

            const params = this.buildQueryParams()

            return this.$axios.get("nasabah/audit-list-nasabah-bank", {
                params,
            }).then(res => {

                this.data = res.data.content
                this.totalRows = res.data.totalElements

                let pg = this.currentPage - 1

                let prev = pg * this.show + 1
                let limit = pg * this.show + this.show
                if (limit > this.totalRows) limit = this.totalRows

                this.totalViewText = "Menampilkan "+ prev + " - " + limit + " dari " + this.totalRows + " entri"
            }).catch(error => {
                this.$helper.parseError(this, error)
                throw error
            })
            .finally(() => {
                this.isBusy = false
            })
        },
        onShowModalApprove(row) {
            this.selectedRow = row;
            this.$bvModal.show("modal-approve")
        },
        onApprove() {
            const id = this.selectedRow.id;
            this.isLoadingApprove = true;
            this.$axios.put(`nasabah/${id}/audit-verify-bank`)
                .then((response) => {
                    this.$helper.toastSucc(this, 'Verifikasi audit rekening berhasil.')
                    this.getItems(this.currentPage)
                    this.$bvModal.hide("modal-approve")
                })
                .catch((error) => {
                    this.$helper.parseError(this, error)
                })
                .finally(() => {
                    this.isLoadingApprove = false;
                })
        },
        onLimitChange(newPerPage) {
            this.perPage = newPerPage
            this.getItems()
        },
        onPageChange(newPage) {
            this.getItems(newPage)
        },
        onSearch() {
            const isInvalidDateRange = this.$helper.isInvalidDateRange(this.filters.startDate, this.filters.endDate)

            if (isInvalidDateRange) {
                this.$helper.toastErr(this, 'Tanggal mulai tidak boleh lebih dari tanggal akhir')
                return;
            }
            
            this.getItems()
        },
        onReset() {
            this.filters = {
                noNasabah: '',
                status: '',
                startDate: null,
                endDate: null,
            }

            this.getItems()
        },
        getStatus(row, isActive) {
            const verifiedBy = row.item.verifiedBy
            const verifiedAt = row.item.verifiedAt

            if (isActive && verifiedBy && verifiedAt) {
                return { text: 'Verified', class: 'status-box-md status-box-success' }
            } else {
                return { text: 'Pending', class: 'status-box-md status-box-warning' }
            }
        },
        handleResize() {
            this.isMobile = window.innerWidth <= 576;
        },
        confirmDownloadExcel() {
            const isInvalidDateRange = this.$helper.isInvalidDateRange(this.filters.startDate, this.filters.endDate)

            if (isInvalidDateRange) {
                this.$helper.toastErr(this, 'Tanggal mulai tidak boleh lebih dari tanggal akhir')
                return;
            }

            this.filePathDownload = '';
            this.$bvModal.show("modal-confirm-excel")
        },
        openModalDownloadExcel() {
            this.isLoadingExcel = true;
            const params = this.buildQueryParams();
            delete params.page
            delete params.size

            this.$axios.get("nasabah/export-audit-nasabah-bank", {
                params,
            }).then(res => {
                this.filePathDownload = res.data.name
                this.$bvModal.show("modal-download-excel")
                this.$bvModal.hide("modal-confirm-excel")
            }).catch(error => {
                this.$helper.parseError(this, error)
            }).finally(() => {
                this.isLoadingExcel = false;
            })
        },
        buildQueryParams() {
            const params = {
                page: this.currentPage - 1,
                size: this.perPage,
            }

            if (!this.$helper.isNullOrEmpty(this.filters.noNasabah)) {
                params.nasabahNo = this.filters.noNasabah;
            }

            if (!this.$helper.isNullOrEmpty(this.filters.status) || this.filters.status == false) {
                params.isChecked = this.filters.status;
            }

            if (!this.$helper.isNullOrEmpty(this.filters.startDate)) {
                params.startDate = this.filters.startDate;
            }

            if (!this.$helper.isNullOrEmpty(this.filters.endDate)) {
                params.endDate = this.filters.endDate;
            }

            return params
        }
    },
    created() {
        this.init();
    },
    mounted() {
        this.handleResize();
        window.addEventListener("resize", this.handleResize);
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.handleResize);
    }
}
</script>